import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Keeps the rows of `list` where they are on screen when a press inside it, or in a row's menu, takes away something
 * above them, as the browser's own scroll anchoring does. WebKit has none: the rows would move under the finger, and
 * the next tap land on another card. Does nothing in a browser that anchors.
 */
export function useKeepRowsInPlace(list: RefObject<HTMLElement | null>): void {
  const anchor = useRef<{ row: Element; top: number } | null>(null);
  useEffect(() => {
    const onPress = (e: MouseEvent) => {
      const rows = list.current;
      if (!rows || !(e.target instanceof Element) || anchors()) return;
      if (!rows.contains(e.target) && e.target.closest('[role="menu"]') === null) return;
      // The first row on screen, as the browser would take it.
      const row = [...rows.children].find((child) => child.getBoundingClientRect().bottom > 0);
      if (!row) return;
      anchor.current = { row, top: row.getBoundingClientRect().top };
      setTimeout(() => {
        anchor.current = null;
      }, 0);
    };
    document.addEventListener("click", onPress, true);
    return () => document.removeEventListener("click", onPress, true);
  }, [list]);
  useLayoutEffect(() => {
    const at = anchor.current;
    if (!at?.row.isConnected) return;
    const moved = at.row.getBoundingClientRect().top - at.top;
    if (moved !== 0) window.scrollBy(0, moved);
    anchor.current = null;
  });
}

/** Whether the browser keeps the page in place itself: `overflow-anchor`, which WebKit lacks. */
const anchors = () => getComputedStyle(document.scrollingElement ?? document.documentElement).overflowAnchor === "auto";
